import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, MapPin, Camera, Send, AlertTriangle } from 'lucide-react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';

import ServiceCategorySelector from './components/ServiceCategorySelector';
import ProviderSelector from './components/ProviderSelector';
import LocationSelector from './components/LocationSelector';
import IssueDescriptionForm from './components/IssueDescriptionForm';
import EvidenceUpload from './components/EvidenceUpload';
import FormProgressIndicator from './components/FormProgressIndicator';
import DuplicateDetectionWarning from './components/DuplicateDetectionWarning';
import { outageService } from '../../services/outageService';
import { useAuth } from '../../contexts/AuthContext';

const ReportOutage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    provider: null,
    category: '',
    title: '',
    description: '',
    severity: 'medium',
    location: {
      city: '',
      country: '',
      coordinates: null
    },
    evidence: []
  });
  
  const [errors, setErrors] = useState({});

  const steps = [
    { id: 1, title: 'Service & Provider', icon: FileText },
    { id: 2, title: 'Location', icon: MapPin },
    { id: 3, title: 'Description', icon: FileText },
    { id: 4, title: 'Evidence', icon: Camera },
    { id: 5, title: 'Review & Submit', icon: Send }
  ];

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData?.provider) {
          newErrors.provider = 'Please select a service provider';
        }
        break;
      case 2:
        if (!formData?.location?.city) {
          newErrors.location = 'Please enter your city';
        }
        break;
      case 3:
        if (!formData?.title?.trim()) {
          newErrors.title = 'Please enter a title for the outage';
        }
        if (!formData?.description?.trim()) {
          newErrors.description = 'Please describe the issue';
        }
        if (formData?.description?.trim()?.length < 10) {
          newErrors.description = 'Description must be at least 10 characters';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps?.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleProviderChange = (provider) => {
    setFormData(prev => ({
      ...prev,
      provider,
      category: provider?.category || ''
    }));
    setErrors(prev => ({ ...prev, provider: '' }));
  };

  const handleLocationChange = (location) => {
    setFormData(prev => ({
      ...prev,
      location
    }));
    setErrors(prev => ({ ...prev, location: '' }));
  };

  const handleDescriptionChange = (title, description, severity) => {
    setFormData(prev => ({
      ...prev,
      title,
      description,
      severity
    }));
    setErrors(prev => ({ 
      ...prev, 
      title: '', 
      description: '' 
    }));
  };

  const handleEvidenceChange = (evidence) => {
    setFormData(prev => ({
      ...prev,
      evidence
    }));
  };

  const handleSubmit = async () => {
    if (!user) {
      setError('You must be logged in to report an outage');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const outageData = {
        provider_id: formData?.provider?.id,
        title: formData?.title,
        description: formData?.description,
        severity: formData?.severity,
        location_city: formData?.location?.city,
        location_country: formData?.location?.country,
        coordinates: formData?.location?.coordinates,
        affected_services: formData?.evidence?.length > 0 ? formData?.evidence : null
      };

      const { data, error } = await outageService?.createOutage(outageData);

      if (error) {
        setError(error?.message);
      } else {
        // Navigate to outage details or dashboard
        navigate('/outage-dashboard', { 
          state: { 
            message: 'Outage reported successfully! Thank you for helping the community.',
            outageId: data?.id
          }
        });
      }
    } catch (err) {
      setError('Failed to submit outage report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <ServiceCategorySelector
              selectedCategory={formData?.category}
              onCategoryChange={(category) => setFormData(prev => ({ ...prev, category }))}
            />
            <ProviderSelector
              selectedProvider={formData?.provider}
              onProviderChange={handleProviderChange}
              error={errors?.provider}
            />
          </div>
        );
      case 2:
        return (
          <LocationSelector
            location={formData?.location}
            onLocationChange={handleLocationChange}
            error={errors?.location}
          />
        );
      case 3:
        return (
          <IssueDescriptionForm
            title={formData?.title}
            description={formData?.description}
            severity={formData?.severity}
            onDescriptionChange={handleDescriptionChange}
            onSeverityChange={(severity) => setFormData(prev => ({ ...prev, severity }))}
            affectedServices={formData?.affectedServices || []}
            onAffectedServicesChange={(services) => setFormData(prev => ({ ...prev, affectedServices: services }))}
            errors={{ title: errors?.title, description: errors?.description }}
          />
        );
      case 4:
        return (
          <EvidenceUpload
            evidence={formData?.evidence}
            onEvidenceChange={handleEvidenceChange}
            attachments={formData?.evidence || []}
            onAttachmentsChange={handleEvidenceChange}
            error={errors?.evidence}
          />
        );
      case 5:
        return (
          <div className="space-y-6">
            {/* Review Summary */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Review Your Report</h3>
              
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Service Provider</dt>
                  <dd className="text-sm text-gray-900">{formData?.provider?.name}</dd>
                </div>
                
                <div>
                  <dt className="text-sm font-medium text-gray-500">Category</dt>
                  <dd className="text-sm text-gray-900 capitalize">
                    {formData?.category?.replace('_', ' ')}
                  </dd>
                </div>
                
                <div>
                  <dt className="text-sm font-medium text-gray-500">Location</dt>
                  <dd className="text-sm text-gray-900">
                    {formData?.location?.city}, {formData?.location?.country}
                  </dd>
                </div>
                
                <div>
                  <dt className="text-sm font-medium text-gray-500">Severity</dt>
                  <dd className="text-sm text-gray-900 capitalize">{formData?.severity}</dd>
                </div>
                
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Title</dt>
                  <dd className="text-sm text-gray-900">{formData?.title}</dd>
                </div>
                
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Description</dt>
                  <dd className="text-sm text-gray-900">{formData?.description}</dd>
                </div>
                
                {formData?.evidence?.length > 0 && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Evidence</dt>
                    <dd className="text-sm text-gray-900">
                      {formData?.evidence?.length} file(s) attached
                    </dd>
                  </div>
                )}
              </dl>
            </div>
            <DuplicateDetectionWarning 
              provider={formData?.provider}
              title={formData?.title}
              location={formData?.location}
              duplicateReports={[]}
              onViewExisting={() => {}}
              onContinueAnyway={() => {}}
              onCancel={() => navigate('/outage-dashboard')}
            />
            {error && (
              <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Report Service Outage</h1>
          <p className="text-gray-600 mt-2">
            Help your community by reporting service disruptions and outages.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <FormProgressIndicator
              steps={steps}
              currentStep={currentStep}
              totalSteps={steps?.length}
              onStepClick={setCurrentStep}
            />
          </div>

          <div className="px-6 py-8">
            {renderStepContent()}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 1}
            >
              Previous
            </Button>

            <div className="flex space-x-3">
              {currentStep < steps?.length ? (
                <Button onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading ? 'Submitting...' : 'Submit Report'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportOutage;